'use client';

/**
 * CaseRow — single case line inside a GroupCard. Tickbox on the left,
 * parties + case number + court + next-hearing on the right. Mono for
 * identifiers; editorial body for names.
 */

import type { AdvocateSearchCase } from '../types';

interface CaseRowProps {
  case_: AdvocateSearchCase;
  selected: boolean;
  disabled?: boolean;
  onToggle: (next: boolean) => void;
}

const COURT_LABEL: Record<string, string> = {
  district: 'District',
  high_court: 'High Court',
  supreme_court: 'Supreme Court',
};

function caseIdentifier(c: AdvocateSearchCase): string {
  const parts: string[] = [];
  if (c.case_type) parts.push(c.case_type);
  if (c.case_number) parts.push(c.case_number);
  if (c.case_year) parts.push(c.case_year);
  if (parts.length === 0 && c.cnr_number) return c.cnr_number;
  return parts.join('/');
}

function formatDate(iso?: string): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return null;
  return `${d}-${m}-${y}`;
}

export default function CaseRow({ case_: c, selected, disabled = false, onToggle }: CaseRowProps) {
  const id = caseIdentifier(c);
  const parties = [c.petitioner, c.respondent].filter(Boolean).join(' · vs · ');
  const court = c.court_type ? COURT_LABEL[c.court_type] ?? c.court_type : null;
  const next = formatDate(c.next_hearing_date);
  const last = formatDate(c.last_hearing_date);

  return (
    <li className="flex items-start gap-3 py-3 text-sm">
      <input
        type="checkbox"
        checked={selected}
        disabled={disabled}
        onChange={e => onToggle(e.target.checked)}
        className="mt-1 h-4 w-4 flex-none rounded border-line text-primary-700 focus:ring-primary-300 disabled:opacity-50"
        aria-label={`Include case ${id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="mono text-ink text-xs tabular-nums">{id}</span>
          {court && (
            <span className="text-xs text-ink-4">· {court}{c.court_name ? ` · ${c.court_name}` : ''}</span>
          )}
          {c.is_active && (
            <span className="text-xs font-medium text-india-green-600">· active</span>
          )}
        </div>
        {parties && (
          <div className="mt-0.5 text-ink-2 truncate">{parties}</div>
        )}
        {(next || last || c.status) && (
          <div className="mt-0.5 text-xs text-ink-4">
            {next && <>Next: <span className="mono">{next}</span></>}
            {!next && last && <>Last: <span className="mono">{last}</span></>}
            {c.status && <span className="ml-2">· {c.status}</span>}
          </div>
        )}
      </div>
    </li>
  );
}
